import angular from 'angular';
import { IStateProvider, IStateService } from 'angular-ui-router';
import { STATE_MAPPINGS } from '@Shared.Angular/@types/stateMappings';
import FlowModelType from '@Shared.Angular/@types/core/contracts/constants/flowModelType';
import { FlowModelCreationType } from './components/FlowModelFromXPage';
import { ProcedureCreationType } from './components/ProcedureFromX';
import { SharedAngular } from '@Client/@types/sharedAngular';
import { FlowinglyPermissions } from '@Shared.Angular/flowingly.services/flowingly.constants';

angular.module('flowingly.runner.creation-hub').config(config);

config.$inject = ['$stateProvider'];

// Abstract container states are not navigable so are not in STATE_MAPPINGS
const CREATION_HUB_ABSTRACT_STATE = 'app.runner.creation-hub';
const CREATION_HUB_PROCESS_MAP_ABSTRACT_STATE =
  'app.runner.creation-hub.process-map';
const CREATION_HUB_WORKFLOW_ABSTRACT_STATE = 'app.runner.creation-hub.workflow';
const CREATION_HUB_PROCEDURE_ABSTRACT_STATE =
  'app.runner.creation-hub.procedure';

const FLOW_MODEL_CREATION_TEMPLATE =
  '<flow-model-from-x-page flow-model-type="$ctrl.flowModelType" creation-type="$ctrl.creationType"></flow-model-from-x-page>';
const PROCEDURE_CREATION_TEMPLATE =
  '<procedure-from-x-page creation-type="$ctrl.creationType"></procedure-from-x-page>';

type FlowModelCreationStateDefinition = {
  stateName: string;
  url: string;
  title: string;
  creationType: FlowModelCreationType;
};

type ProcedureCreationStateDefinition = {
  stateName: string;
  url: string;
  title: string;
  creationType: ProcedureCreationType;
};

const PROCESS_MAP_FLOW_MODEL_CREATION_STATES: FlowModelCreationStateDefinition[] =
  [
    {
      stateName: STATE_MAPPINGS.creationHubProcessMapFromScratch,
      url: '/from-scratch',
      title: 'Process Map from Scratch',
      creationType: FlowModelCreationType.FromScratch
    },
    {
      stateName: STATE_MAPPINGS.creationHubProcessMapFromText,
      url: '/from-text',
      title: 'Process Map from Text',
      creationType: FlowModelCreationType.FromText
    },
    {
      stateName: STATE_MAPPINGS.creationHubProcessMapFromImage,
      url: '/from-image',
      title: 'Process Map from Image',
      creationType: FlowModelCreationType.FromImage
    },
    {
      stateName: STATE_MAPPINGS.creationHubProcessMapFromCsv,
      url: '/from-csv',
      title: 'Process Map from CSV',
      creationType: FlowModelCreationType.FromCsv
    },
    {
      stateName: STATE_MAPPINGS.creationHubProcessMapFromVideo,
      url: '/from-video',
      title: 'Generate Process Map from Video',
      creationType: FlowModelCreationType.FromVideo
    },
    {
      stateName: STATE_MAPPINGS.creationHubProcessMapFromAudio,
      url: '/from-audio',
      title: 'Generate Process Map from Audio',
      creationType: FlowModelCreationType.FromAudio
    },
    {
      stateName: STATE_MAPPINGS.creationHubProcessMapFromRecording,
      url: '/from-recording',
      title: 'Process Map from Recording',
      creationType: FlowModelCreationType.FromRecording
    }
  ];

const WORKFLOW_FLOW_MODEL_CREATION_STATES: FlowModelCreationStateDefinition[] =
  [
    {
      stateName: STATE_MAPPINGS.creationHubWorkflowFromScratch,
      url: '/from-scratch',
      title: 'Workflow from Scratch',
      creationType: FlowModelCreationType.FromScratch
    },
    {
      stateName: STATE_MAPPINGS.creationHubWorkflowFromText,
      url: '/from-text',
      title: 'Workflow from Text',
      creationType: FlowModelCreationType.FromText
    },
    {
      stateName: STATE_MAPPINGS.creationHubWorkflowFromImage,
      url: '/from-image',
      title: 'Workflow from Image',
      creationType: FlowModelCreationType.FromImage
    },
    {
      stateName: STATE_MAPPINGS.creationHubWorkflowFromCsv,
      url: '/from-csv',
      title: 'Workflow from CSV',
      creationType: FlowModelCreationType.FromCsv
    },
    {
      stateName: STATE_MAPPINGS.creationHubWorkflowFromVideo,
      url: '/from-video',
      title: 'Generate Workflow from Video',
      creationType: FlowModelCreationType.FromVideo
    },
    {
      stateName: STATE_MAPPINGS.creationHubWorkflowFromAudio,
      url: '/from-audio',
      title: 'Generate Workflow from Audio',
      creationType: FlowModelCreationType.FromAudio
    },
    {
      stateName: STATE_MAPPINGS.creationHubWorkflowFromRecording,
      url: '/from-recording',
      title: 'Workflow from Recording',
      creationType: FlowModelCreationType.FromRecording
    }
  ];

const PROCEDURE_CREATION_STATES: ProcedureCreationStateDefinition[] = [
  {
    stateName: STATE_MAPPINGS.creationHubProcedureFromScratch,
    url: '/from-scratch',
    title: 'SOP from Scratch',
    creationType: ProcedureCreationType.FromScratch
  },
  {
    stateName: STATE_MAPPINGS.creationHubProcedureFromExtension,
    url: '/capture',
    title: 'Capture SOP',
    creationType: ProcedureCreationType.FromExtension
  },
  {
    stateName: STATE_MAPPINGS.creationHubProcedureFromExtensionWithAi,
    url: '/capture-with-ai',
    title: 'Capture SOP with AI',
    creationType: ProcedureCreationType.FromExtensionWithAI
  },
  {
    stateName: STATE_MAPPINGS.creationHubProcedureFromRecording,
    url: '/from-recording',
    title: 'SOP from Recording',
    creationType: ProcedureCreationType.FromRecording
  },
  {
    stateName: STATE_MAPPINGS.creationHubProcedureFromVideo,
    url: '/upload-video',
    title: 'SOP from Video',
    creationType: ProcedureCreationType.FromUpload
  }
];

function createFlowModelCreationController(
  flowModelType: FlowModelType,
  creationType: FlowModelCreationType
) {
  return function (this: {
    flowModelType: FlowModelType;
    creationType: FlowModelCreationType;
  }) {
    this.flowModelType = flowModelType;
    this.creationType = creationType;
  };
}

function createProcedureCreationController(
  creationType: ProcedureCreationType
) {
  return function (this: { creationType: ProcedureCreationType }) {
    this.creationType = creationType;
  };
}

function registerFlowModelCreationStates(
  $stateProvider: IStateProvider,
  flowModelType: FlowModelType,
  stateDefinitions: FlowModelCreationStateDefinition[]
): void {
  stateDefinitions.forEach((stateDefinition) => {
    $stateProvider.state(stateDefinition.stateName, {
      url: stateDefinition.url,
      params: { title: stateDefinition.title },
      controller: createFlowModelCreationController(
        flowModelType,
        stateDefinition.creationType
      ),
      controllerAs: '$ctrl',
      template: FLOW_MODEL_CREATION_TEMPLATE
    });
  });
}

function registerProcedureCreationStates(
  $stateProvider: IStateProvider,
  stateDefinitions: ProcedureCreationStateDefinition[]
): void {
  stateDefinitions.forEach((stateDefinition) => {
    $stateProvider.state(stateDefinition.stateName, {
      url: stateDefinition.url,
      params: { title: stateDefinition.title },
      controller: createProcedureCreationController(
        stateDefinition.creationType
      ),
      controllerAs: '$ctrl',
      template: PROCEDURE_CREATION_TEMPLATE
    });
  });
}

function config($stateProvider: IStateProvider): void {
  $stateProvider.state(CREATION_HUB_ABSTRACT_STATE, {
    url: 'create',
    abstract: true,
    template: '<div ui-view></div>'
  });

  $stateProvider.state(STATE_MAPPINGS.creationHub, {
    url: '',
    params: { title: 'Creation Hub' },
    template: '<creation-hub-page></creation-hub-page>',
    onEnter: [
      'APP_CONFIG',
      '$state',
      '$timeout',
      'permissionsService',
      function (
        APP_CONFIG: SharedAngular.APP_CONFIG,
        $state: IStateService,
        $timeout: angular.ITimeoutService,
        permissionsService: SharedAngular.PermissionsService
      ) {
        if (
          !permissionsService.currentUserHasAnyOfPermissions([
            FlowinglyPermissions.FLOWMODEL_CREATE,
            FlowinglyPermissions.PROCEDURE_CREATE
          ])
        ) {
          return;
        }
        if (
          APP_CONFIG.enableProcedures &&
          !APP_CONFIG.allowProcessMap &&
          !APP_CONFIG.allowWorkflow
        ) {
          $timeout(() => $state.go(STATE_MAPPINGS.creationHubProcedure), 0);
        } else if (
          APP_CONFIG.allowWorkflow &&
          !APP_CONFIG.allowProcessMap &&
          !APP_CONFIG.enableProcedures
        ) {
          $timeout(() => $state.go(STATE_MAPPINGS.creationHubWorkflow), 0);
        } else if (
          APP_CONFIG.allowProcessMap &&
          !APP_CONFIG.allowWorkflow &&
          !APP_CONFIG.enableProcedures
        ) {
          $timeout(() => $state.go(STATE_MAPPINGS.creationHubProcessMap), 0);
        }
      }
    ]
  });

  $stateProvider.state(CREATION_HUB_PROCESS_MAP_ABSTRACT_STATE, {
    url: '/process-map',
    params: { flowModelType: FlowModelType.ProcessMap },
    abstract: true,
    template: '<div ui-view></div>',
    onEnter: [
      'APP_CONFIG',
      '$state',
      function (APP_CONFIG: SharedAngular.APP_CONFIG, $state: IStateService) {
        if (!APP_CONFIG.allowProcessMap) {
          $state.go(STATE_MAPPINGS.creationHub);
        }
      }
    ]
  });

  $stateProvider.state(STATE_MAPPINGS.creationHubProcessMap, {
    url: '',
    params: { title: 'Create Process Map' },
    template: '<create-process-map-hub-page></create-process-map-hub-page>'
  });

  $stateProvider.state(CREATION_HUB_WORKFLOW_ABSTRACT_STATE, {
    url: '/workflow',
    params: { flowModelType: FlowModelType.Workflow },
    abstract: true,
    template: '<div ui-view></div>',
    onEnter: [
      'APP_CONFIG',
      '$state',
      function (APP_CONFIG: SharedAngular.APP_CONFIG, $state: IStateService) {
        if (!APP_CONFIG.allowWorkflow) {
          $state.go(STATE_MAPPINGS.creationHub);
        }
      }
    ]
  });

  $stateProvider.state(STATE_MAPPINGS.creationHubWorkflow, {
    url: '',
    params: { title: 'Create Workflow' },
    template: '<create-workflow-hub-page></create-workflow-hub-page>'
  });

  registerFlowModelCreationStates(
    $stateProvider,
    FlowModelType.Workflow,
    WORKFLOW_FLOW_MODEL_CREATION_STATES
  );

  registerFlowModelCreationStates(
    $stateProvider,
    FlowModelType.ProcessMap,
    PROCESS_MAP_FLOW_MODEL_CREATION_STATES
  );

  $stateProvider.state(CREATION_HUB_PROCEDURE_ABSTRACT_STATE, {
    url: '/sop',
    abstract: true,
    template: '<div ui-view></div>',
    onEnter: [
      'APP_CONFIG',
      '$state',
      function (APP_CONFIG: SharedAngular.APP_CONFIG, $state: IStateService) {
        if (!APP_CONFIG.enableProcedures) {
          $state.go(STATE_MAPPINGS.creationHub);
        }
      }
    ]
  });

  $stateProvider.state(STATE_MAPPINGS.creationHubProcedure, {
    url: '',
    params: { title: 'Create SOP' },
    template: '<create-procedure-hub-page></create-procedure-hub-page>'
  });

  registerProcedureCreationStates($stateProvider, PROCEDURE_CREATION_STATES);
}
